// The hangup wrap-up (docs/design/CALL-POP-KRISP-BAR.md lane 4; lane 4 owns this file): in the say-next box once the
// call has ended, the two lines in the caller's words first, the next step, the action items with their owner, the
// suggested disposition as one button to confirm it, and a follow-up chip. Every row is a fixed reservation inside the
// box, the same height as say-next: while the job writes, the status line says so and the rows hold quiet placeholders;
// when it lands, text fills the same rows. Nothing inside or outside the box moves, nothing animates, and the only
// data-slot is the box's own. Icons through ui/Icon.tsx only. CallWindowStyles concatenates WRAPUP_CSS.
import { useState } from 'react';
import { CalendarClock, Check, ThumbsDown, ThumbsUp } from 'lucide-react';
import type { CallSummary, CallSummaryActionItem, ContextCardDispositionOption } from '@bcio-voice/contracts';
import { Icon } from '../../ui/Icon.tsx';
import { ApiRequestError, type DataEnvelope } from '../../lib/api.ts';
import { coachOpening } from '../../call/coach/coachOpening.ts';

export interface WrapUpProps {
  summary: CallSummary | null;
  /** The summary job is still writing: a reserved line says so. */
  waiting: boolean;
  /** Off the coach's hours the job waits for its opening: this ISO time, shown as "Wrap-up at 7:00", or "Wrap-up
   * Monday 7:00" when it is more than a day away. */
  until?: string | null;
  /** Still awaited after the window's read schedule ran out: the status says "Wrap-up pending" (with waiting) and the
   * box keeps say-next's place until call.summary_ready brings it (review round 3). */
  stalled?: boolean;
  onDisposition(id: string): void;
  onFeedback(vote: 'up' | 'down'): void;
  /** The dispositions this call can be logged with (the CRM pane's crm.dispositions), for the suggested one's label.
   * Given, the suggestion is offered only when it is one of them: an empty list (a card carried from the last call, a
   * generic or contact card, no card) offers nothing, as the pane does. Left out, any suggestion is offered. */
  dispositions?: readonly ContextCardDispositionOption[];
  /** The disposition already saved for this call, if any (the CRM pane's dispo.selected). */
  chosen?: string | null;
  /** The disposition being saved right now (the CRM pane's dispo.busy). */
  busy?: string | null;
  /** The viewer's vote on this summary; omitted, the box remembers the last click itself. */
  voted?: 'up' | 'down' | null;
  /** The follow-up chip becomes a button when this is given (for example: open the CRM's follow-up task). */
  onFollowUp?(): void;
}

/** Say-next's exact box: its height is .cw-say's --cw-say-h (CallWindowView.tsx, the sum of say-next's reserved rows,
 * the same at every pane width). The rows: header, line 1, line 2, next step, action items (the rest, scrolling
 * inside), and the disposition button beside the follow-up chip. */
export const WRAPUP_CSS = `
.cw-wrapup { height: var(--cw-say-h); box-sizing: border-box; min-height: 0; min-width: 0; overflow: hidden; display: grid; grid-template-rows: 16px 20px 20px 20px minmax(0, 1fr) 26px; row-gap: var(--space-1); }
.cw-wrapup p, .cw-wrapup ul { margin: 0; }
.cw-wrapup-top { display: flex; align-items: center; gap: var(--space-3); min-width: 0; height: 16px; font-size: var(--text-min); line-height: 16px; color: var(--ink-3); }
.cw-wrapup-head { flex: none; font-weight: var(--wght-strong); letter-spacing: var(--track-small); }
.cw-wrapup-status { flex: 1 1 0; min-width: 0; height: 16px; text-align: start; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cw-wrapup-status.is-warn { color: var(--warn); }
.cw-wrapup-votes { flex: none; box-sizing: border-box; width: calc(2 * 16px + var(--space-2)); height: 16px; display: inline-flex; align-items: center; justify-content: flex-end; gap: var(--space-2); overflow: hidden; }
.cw-wrapup-vote { flex: none; box-sizing: border-box; width: 16px; height: 16px; padding: 0; margin: 0; border: 0; border-radius: var(--radius-control); background: transparent; color: var(--ink-3); display: inline-flex; align-items: center; justify-content: center; font-size: 14px; line-height: 0; cursor: pointer; }
.cw-wrapup-vote:hover { color: var(--ink); }
.cw-wrapup-vote[aria-pressed='true'] { color: var(--brass); }
/* Nothing to rate (still written, skipped): the thumbs keep their place but are not drawn, as TipFeedback does. */
.cw-wrapup-vote:disabled { cursor: default; visibility: hidden; }
.cw-wrapup-line, .cw-wrapup-next { min-width: 0; height: 20px; font-size: var(--text-body); line-height: 20px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cw-wrapup-line-1 { color: var(--ink); font-weight: var(--wght-medium); }
.cw-wrapup-line-2 { color: var(--ink-2); }
.cw-wrapup-next { color: var(--ink); }
.cw-wrapup-label { font-size: var(--text-min); color: var(--ink-3); font-weight: var(--wght-strong); letter-spacing: var(--track-small); margin-inline-end: var(--space-2); }
.cw-wrapup-items { min-height: 0; padding: 0; list-style: none; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; display: grid; grid-auto-rows: 16px; row-gap: 2px; align-content: start; }
.cw-wrapup-item { display: grid; grid-template-columns: 5ch minmax(0, 1fr) minmax(0, max-content); gap: var(--space-2); min-width: 0; font-size: var(--text-meta); line-height: 16px; }
.cw-wrapup-owner { font-size: var(--text-min); color: var(--ink-3); font-weight: var(--wght-strong); letter-spacing: var(--track-small); }
.cw-wrapup-item-text { min-width: 0; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cw-wrapup-item-due { max-width: 16ch; color: var(--ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cw-wrapup-none { font-size: var(--text-meta); line-height: 16px; color: var(--ink-3); }
.cw-wrapup-actions { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--space-2); height: 26px; min-width: 0; }
.cw-wrapup-actions .btn { min-height: 0; height: 26px; min-width: 0; padding: 0 var(--space-2); font-size: var(--text-meta); justify-content: flex-start; overflow: hidden; }
.cw-wrapup-actions .btn[aria-pressed='true'] { border-color: var(--brass); background: var(--brass-tint); }
.cw-wrapup-actions .btn[aria-busy='true'] { border-color: var(--brass); }
.cw-wrapup-follow { box-sizing: border-box; min-height: 0; height: 26px; min-width: 0; overflow: hidden; justify-content: flex-start; }
.cw-wrapup-follow.is-empty { color: var(--ink-3); border-style: dashed; }
button.cw-wrapup-follow { cursor: pointer; font: inherit; font-size: var(--text-min); }
.cw-wrapup-text { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

export const WRAPUP_WAITING = 'Writing the wrap-up';
/** Once the box has said a wrap-up is coming it never hands say-next back; when the reads run out it says this. */
export const WRAPUP_PENDING = 'Wrap-up pending';
/** The box's status when it is given nothing at all. The call window shows that state only after the box already said a
 * wrap-up was coming (review round 3: a promise is never taken back by handing the box back to say-next); when none is
 * coming from the start (Coach was off, wrap-ups are not set up, no transcript grant, a call under 30 seconds or with
 * nothing transcribed) say-next keeps its box (review round 2). */
export const WRAPUP_NONE = 'No wrap-up for this call';

/** "Wrap-up at 7:00": the coach's opening on this browser's clock; "Wrap-up Monday 7:00" when that is more than a day
 * away, the same rule as the coach's status (coachOpening). */
export function wrapUpAtLine(until: string | null | undefined, now: number = Date.now()): string {
  const at = until ? Date.parse(until) : NaN;
  if (!Number.isFinite(at)) return WRAPUP_WAITING;
  const { day, time } = coachOpening(at, now);
  return day ? `Wrap-up ${day} ${time}` : `Wrap-up at ${time}`;
}
/** A skipped call's status. The call window shows too_short and no_transcript only when the box had already said a
 * wrap-up was coming (CallWindowRoot: wrapUpQuietSkip); otherwise say-next keeps its box, as Krisp's contract has calls
 * under 30 seconds get no summary at all (review round 3). */
const SKIPPED: Record<string, { text: string; warn: boolean }> = {
  too_short: { text: 'No wrap-up for a call under 30 seconds', warn: false },
  no_transcript: { text: 'No wrap-up: nothing was transcribed', warn: false },
  provider_unavailable: { text: 'Wrap-up when Coach is back', warn: true },
};
/** too_short and no_transcript: no wrap-up is written, so none takes say-next's box. */
export const wrapUpQuietSkip = (summary: CallSummary | null): boolean => summary?.skipped_reason === 'too_short' || summary?.skipped_reason === 'no_transcript';

/** "callback" without a label on the card reads "Callback"; "meeting_set" reads "Meeting set". */
export function dispositionLabel(id: string, options: readonly ContextCardDispositionOption[] = []): string {
  const known = options.find(o => o.id === id);
  if (known) return known.label;
  const words = id.replace(/[_:-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

/** When to follow up: the next step's date ("Tue, Nov 3"), else our first action item's own words, else null. */
export function followUpWhen(summary: CallSummary | null, locale = 'en-US'): string | null {
  if (!summary) return null;
  if (summary.next_step_at) {
    const at = new Date(summary.next_step_at);
    if (Number.isFinite(at.getTime())) return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' }).format(at);
  }
  const ours = summary.action_items.find(i => i.owner === 'us' && i.due) ?? summary.action_items.find(i => i.due);
  return ours?.due ?? null;
}

/** The id one click on the disposition button sends: the suggestion, unless a disposition is saved or saving, or none
 * was suggested. */
export function dispositionToConfirm(summary: CallSummary | null, chosen: string | null | undefined, busy: string | null | undefined): string | null {
  const suggestion = summary?.suggested_disposition ?? null;
  return suggestion && (chosen ?? null) === null && (busy ?? null) === null ? suggestion : null;
}

/** Thumbs count only for a written summary (not while writing, not for a skipped call). */
export const mayVoteOn = (summary: CallSummary | null): boolean => summary !== null && summary.skipped_reason === null;

function None() {
  return <><span className="cw-dash" aria-hidden="true" /><span className="visually-hidden">None</span></>;
}

/** An empty field in a written wrap-up: the quiet dash on screen, the words for screen readers (review round 2: no
 * "No ..." sentences for the rep to read). */
function Quiet({ words }: { words: string }) {
  return <><span className="cw-dash" aria-hidden="true" /><span className="visually-hidden">{words}</span></>;
}

function Item({ item }: { item: CallSummaryActionItem }) {
  return (
    <li className="cw-wrapup-item">
      <span className="cw-wrapup-owner">{item.owner === 'us' ? 'Us' : 'Them'}</span>
      <span className="cw-wrapup-item-text" title={item.text}>{item.text}</span>
      <span className="cw-wrapup-item-due" title={item.due ?? undefined}>{item.due ?? ''}</span>
    </li>
  );
}

export function WrapUp(props: WrapUpProps) {
  const { summary, waiting } = props;
  const [localVote, setLocalVote] = useState<'up' | 'down' | null>(null);
  const vote = props.voted !== undefined ? props.voted : localVote;
  const written = summary !== null && summary.skipped_reason === null;
  const pending = !summary && waiting;
  const skipped = summary?.skipped_reason ? SKIPPED[summary.skipped_reason] ?? null : null;
  const status = pending ? (props.until ? wrapUpAtLine(props.until) : props.stalled ? WRAPUP_PENDING : WRAPUP_WAITING) : skipped?.text ?? (summary ? '' : WRAPUP_NONE);
  const bar = (width: 'cw-ph-60' | 'cw-ph-45' | 'cw-ph-35') => (pending ? <span className={`cw-ph ${width}`} aria-hidden="true" /> : <None />);
  const line1 = written ? summary.lines[0] ?? null : null;
  const line2 = written ? summary.lines[1] ?? null : null;
  const options = props.dispositions ?? [];
  const suggested = summary?.suggested_disposition ?? null;
  // Only a disposition this call can be logged with (bcio-net drops one with no candidate behind it).
  const suggestion = suggested !== null && (props.dispositions === undefined || options.some(o => o.id === suggested)) ? suggested : null;
  const chosen = props.chosen ?? null;
  const busy = props.busy ?? null;
  const confirmed = chosen !== null;
  const dispoText = confirmed ? `Logged as ${dispositionLabel(chosen, options)}` : suggestion ? `Log as ${dispositionLabel(suggestion, options)}` : 'No disposition suggested';
  const when = written ? followUpWhen(summary) : null;
  const followText = when ? `Follow up ${when}` : 'No follow-up set';
  const cast = (v: 'up' | 'down') => { if (!mayVoteOn(summary)) return; setLocalVote(v); props.onFeedback(v); };
  return (
    <section className="cw-wrapup" data-slot="wrapup" aria-label="Wrap-up" aria-busy={pending || undefined}>
      <div className="cw-wrapup-top">
        <span className="cw-wrapup-head">Wrap-up</span>
        <span className={`cw-wrapup-status${skipped?.warn ? ' is-warn' : ''}`} role="status" title={status || undefined}>{status}</span>
        <span className="cw-wrapup-votes" role="group" aria-label="Rate this wrap-up">
          <button type="button" className="cw-wrapup-vote" aria-label="Good wrap-up" aria-pressed={vote === 'up'} disabled={!written} onClick={() => cast('up')}>
            <Icon icon={ThumbsUp} size="1em" />
          </button>
          <button type="button" className="cw-wrapup-vote" aria-label="Poor wrap-up" aria-pressed={vote === 'down'} disabled={!written} onClick={() => cast('down')}>
            <Icon icon={ThumbsDown} size="1em" />
          </button>
        </span>
      </div>
      <p className="cw-wrapup-line cw-wrapup-line-1" title={line1 ?? undefined}>{line1 ?? bar('cw-ph-60')}</p>
      <p className="cw-wrapup-line cw-wrapup-line-2" title={line2 ?? undefined}>{line2 ?? (pending ? bar('cw-ph-45') : '')}</p>
      <p className="cw-wrapup-next" title={written && summary.next_step ? summary.next_step : undefined}>
        <span className="cw-wrapup-label">Next</span>
        {written && summary.next_step ? summary.next_step : bar('cw-ph-35')}
      </p>
      <ul className="cw-wrapup-items" aria-label="Action items">
        {written && summary.action_items.length
          ? summary.action_items.map((item, i) => <Item key={`${i}-${item.text}`} item={item} />)
          : <li className="cw-wrapup-none">{written ? <Quiet words="No action items" /> : ''}</li>}
      </ul>
      <div className="cw-wrapup-actions">
        <button
          type="button"
          className="btn"
          aria-pressed={confirmed}
          aria-busy={(suggestion !== null && busy === suggestion) || undefined}
          disabled={!confirmed && suggestion === null}
          title={dispoText}
          // Not disabled while it saves: a button dimming for a round trip would flash. A second press waits.
          onClick={() => { const id = suggestion !== null ? dispositionToConfirm(summary, chosen, busy) : null; if (id) props.onDisposition(id); }}
        >
          <Icon icon={Check} size="1em" />
          {/* While the wrap-up is written there is nothing to say yet: a quiet bar, the words for screen readers. With
              nothing to log, the quiet dash. */}
          <span className="cw-wrapup-text">{pending && !confirmed ? <><span className="cw-ph cw-ph-45" aria-hidden="true" /><span className="visually-hidden">{dispoText}</span></> : !confirmed && suggestion === null ? <Quiet words={dispoText} /> : dispoText}</span>
        </button>
        {props.onFollowUp && when ? (
          <button type="button" className="chip cw-wrapup-follow" title={followText} onClick={() => props.onFollowUp?.()}>
            <Icon icon={CalendarClock} size="1em" />
            <span className="cw-wrapup-text">{followText}</span>
          </button>
        ) : (
          <span className={`chip cw-wrapup-follow${when ? '' : ' is-empty'}`} title={followText}>
            <Icon icon={CalendarClock} size="1em" />
            <span className="cw-wrapup-text">{pending ? <><span className="cw-ph cw-ph-35" aria-hidden="true" /><span className="visually-hidden">{followText}</span></> : when ? followText : <Quiet words={followText} />}</span>
          </span>
        )}
      </div>
    </section>
  );
}

/** What the wrap-up box shows for one read of /calls/{id}/summary, and when to ask again. until: off the coach's hours,
 * the opening the job waits for (only present then). */
export interface WrapUpLoad { summary: CallSummary | null; waiting: boolean; retryInMs: number | null; until?: string }
export const WRAPUP_RETRY_MS = 3000;

/**
 * Reads the call's wrap-up for the call window host (lane 2 wires it on call end and on the call.summary_ready event):
 * 200 is the summary; 404 summary_pending is still writing (ask again), with until off the coach's hours; a server error
 * or no answer is treated as still writing too; summary_not_found, summary_unconfigured or no access mean there is none,
 * so nothing waits. ask: POST instead of GET, which also asks the API to write it (only while the rep has Coach on:
 * the wrap-up sends the transcript and card to the coach's provider once more; apps/api/src/summary/index.ts).
 */
export async function loadWrapUp(api: { request<T>(path: string, init?: RequestInit): Promise<T> }, callId: string, signal?: AbortSignal, ask = false): Promise<WrapUpLoad> {
  try {
    const init: RequestInit = { ...(ask ? { method: 'POST' } : {}), ...(signal ? { signal } : {}) };
    const r = await api.request<DataEnvelope<CallSummary>>(`/calls/${encodeURIComponent(callId)}/summary`, init);
    return { summary: r.data, waiting: false, retryInMs: null };
  } catch (error) {
    if (!(error instanceof ApiRequestError)) throw error;
    if (error.code === 'summary_pending' && error.until) return { summary: null, waiting: true, retryInMs: WRAPUP_RETRY_MS, until: error.until };
    if (error.code === 'summary_pending' || error.status === 0 || (error.status >= 500 && error.code !== 'summary_unconfigured')) return { summary: null, waiting: true, retryInMs: WRAPUP_RETRY_MS };
    return { summary: null, waiting: false, retryInMs: null };
  }
}
